import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { bibliographyColumnsVisibility } from "@/config/dashboard/bibliography";
import { documentsColumnsVisibility } from "@/config/dashboard/documents";
import { FormLayoutElement } from "@/config/forms/general";
import {
  Author,
  Bibliography,
  Document,
  DocumentInBibliography,
} from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  BibliographyLinkGridCard,
  DocumentLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import DocumentInBibliographyDrawer from "./drawer/DocumentInBibliographyDrawer";

interface ComplexLinkProps {
  id: string;
  document: Document | undefined;
  bibliography: Bibliography | undefined;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

const columnsForBibliography = [
  {
    accessorKey: "bibliography.bibliographieDescriptionNumeroBibliographie",
    header: "bibliographieDescriptionNumeroBibliographie",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<DocumentInBibliography> }) => {
      const attachments =
        row.original.bibliography?.attachments ??
        row.original.attachments ??
        [];
      return (
        <ImageCell
          value={""}
          attachments={attachments}
          domain={"bibliography"}
        />
      );
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAnnee",
    header: "bibliographieDescriptionAnnee",
    size: 50,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
];

const documentColumns = [
  {
    accessorKey: "document.documentsDescriptionNumeroEnregistrement",
    header: "N°",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<DocumentInBibliography> }) => {
      const attachments =
        row.original.document?.attachments ?? row.original.attachments ?? [];
      return (
        <ImageCell
          value=""
          attachments={attachments}
          domain="document"
        />
      );
    },
  },
  {
    accessorKey: "document.documentsDescriptionTitre",
    header: "Titre",
    size: 300,
    cell: (props: any) => {
      return <HtmlCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "document.documentsDescriptionType",
    header: "Type",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
];

/**
 * DocumentInBibliographyLinks component
 * @returns {JSX.Element}
 */
const DocumentInBibliographyLinks = ({
  id,
  document,
  fields,
  type,
  bibliography,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "documentInBibliography",
    type,
  );

  const { mutate: deleteDocumentInBibliography } = useDeleteComplexLink(
    id,
    "documentInBibliography",
    type,
  );

  const [searchContent, setSearchContent] = useState("");

  const [isAddMode, setIsAddMode] = useState(false);
  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteDocumentInBibliography({
        id,
        relation: "documentInBibliography",
      });
    },
    [deleteDocumentInBibliography],
  );

  const renderDocumentInBibliographyRowActions = useCallback(
    (data: DocumentInBibliography) => {
      const preview =
            type === "document"
              ? {
                  previewType: "bibliography" as const,
                  previewId: data.bibliography.id,
                  previewElements: [
                    {
                      label: t("bibliographieDescriptionNumeroBibliographie"),
                      value:
                        data.bibliography
                          .bibliographieDescriptionNumeroBibliographie,
                      type: "text" as const,
                    },
                    {
                      label: t("bibliographieDescriptionResumeLong"),
                      value: data.bibliography.bibliographieDescriptionResumeLong,
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.bibliography.attachments,
                      type: "image" as const,
                    },
                  ],
                }
              : type === "bibliography"
                ? {
                    previewType: "document" as const,
                    previewId: data.document.id,
                    previewElements: [
                      {
                        label: t("documentsDescriptionNumeroEnregistrement"),
                        value:
                          data.document.documentsDescriptionNumeroEnregistrement,
                        type: "text" as const,
                      },
                      {
                        label: t("documentsDescriptionLegendeRedigee"),
                        value: data.document.documentsDescriptionLegendeRedigee,
                        type: "richtext" as const,
                      },
                      {
                        label: t("attachments"),
                        value: data.document.attachments,
                        type: "image" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) => (
                <DocumentInBibliographyDrawer
                  key={data.id}
                  fields={fields}
                  queryId={id}
                  type={type}
                  data={data}
                  open={open}
                  onOpenChange={onOpenChange}
                />
              )}
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<DocumentInBibliography, any>[] = useMemo(() => {
    return [
      ...(type === "document"
        ? columnsForBibliography.map((column) => ({
            ...column,
            header: t(column.header),
          }))
        : documentColumns.map((column) => ({
            ...column,
            header: t(column.header),
          }))),
      {
        accessorKey: "documentsDansBibliographieDescriptionReproduitPage",
        header: t("documentsDansBibliographieDescriptionReproduitPage"),
      },
      {
        accessorKey: "documentsDansBibliographieDescriptionCitePage",
        header: t("documentsDansBibliographieDescriptionCitePage"),
      },
      {
        accessorKey: "documentsDansBibliographieDescriptionReproductionCouleur",
        header: t("documentsDansBibliographieDescriptionReproductionCouleur"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? vt("bo.links.yes", "Oui") : vt("bo.links.no", "Non")} />;
        },
      },
      {
        accessorKey: "documentsDansBibliographieDescriptionNonPagine",
        header: t("documentsDansBibliographieDescriptionNonPagine"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? vt("bo.links.yes", "Oui") : vt("bo.links.no", "Non")} />;
        },
      },
      createActionsColumnDef<DocumentInBibliography>(({ row }) =>
        renderDocumentInBibliographyRowActions(row.original),
      ),
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "document") {
      return (
        <DocumentToBibliographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          document={document}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderDocumentInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedBibliographies")}
          layoutBackLabel={t("linkedEntries")}
          setIsAddMode={setIsAddMode}
        />
      );
    } else {
      return (
        <BibliographyToDocumentLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          bibliography={bibliography}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderDocumentInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedDocuments")}
          layoutBackLabel={t("linkedEntries")}
          setIsAddMode={setIsAddMode}
        />
      );
    }
  }, [
    type,
    id,
    relatedData,
    fields,
    document,
    isAddMode,
    columns,
    searchContent,
    bibliography,
  ]);

  return <div className="w-full">{datatableConf}</div>;
};

type DocumentToBibliographyLinksProps =
  LinksTableProps<DocumentInBibliography> & {
    document: Document | undefined;
    bibliography?: never;
  };

type BibliographyToDocumentLinksProps =
  LinksTableProps<DocumentInBibliography> & {
    bibliography: Bibliography | undefined;
    document?: never;
  };

//Composant pour Document
const DocumentToBibliographyLinks = ({
  id,
  relatedData,
  fields,
  document,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: DocumentToBibliographyLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "document-links-bibliography-add";
  const STORAGE_KEY_LINKED = "document-links-bibliography-linked";
  const defaultVisibility = { ...bibliographyColumnsVisibility, actions: true };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resultType: "bibliography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographiesDescriptionNumeroBibliographie"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.bibliographieDescriptionNumeroBibliographie}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographiesDescriptionAnnee"),
        size: 100,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.bibliographieDescriptionAnnee} />
          );
        },
      },
      {
        accessorKey: "relatedAuthor",
        header: t("bibliographiesDescriptionAuteur"),
        size: 200,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-3 text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.relatedAuthor
                  .map((author: Author) => author.artistesAuteursDescriptionNom)
                  .join(", ")}
              </p>
            </div>
          );
        },
      },
      createActionsColumnDef<Bibliography>(({ row }) => {
          const data = row.original as Bibliography;
          if (!document) return null;
          return (
            <LinkDrawerRowActions
              previewType="bibliography"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("bibliographieDescriptionNumeroBibliographie"),
                  value: data.bibliographieDescriptionNumeroBibliographie,
                  type: "text",
                },
                {
                  label: t("bibliographieDescriptionResumeLong"),
                  value: data.bibliographieDescriptionResumeLong,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <DocumentInBibliographyDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={document.id}
                  type="document"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography: data,
                    document: document,
                    documentsDansBibliographieDescriptionReproduitPage: "",
                    documentsDansBibliographieDescriptionCitePage: "",
                    documentsDansBibliographieDescriptionPhotographe: "",
                    documentsDansBibliographieDescriptionReproductionCouleur:
                      false,
                    documentsDansBibliographieDescriptionNonPagine: false,
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [document, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsBibliographies} data={notRelatedBibliographies?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notRelatedBibliographies?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAdd, onColumnOrderChange: setColumnsOrderAdd, state: { columnVisibility: columnVisibilityAdd, columnOrder: columnsOrderAdd } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinked, onColumnOrderChange: setColumnsOrderLinked, state: { columnVisibility: columnVisibilityLinked, columnOrder: columnsOrderLinked } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <BibliographyLinkGridCard
                  item={row.bibliography}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <DocumentInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="document"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

// Composant pour Bibliographies
const BibliographyToDocumentLinks = ({
  id,
  relatedData,
  fields,
  bibliography,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: BibliographyToDocumentLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_D = "bibliography-links-document-add";
  const STORAGE_KEY_LINKED_D = "bibliography-links-document-linked";
  const defaultVisibilityD = {
    ...documentsColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityAddD, setColumnVisibilityAddD] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_D);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityD, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityD };
    });
  const [columnVisibilityLinkedD, setColumnVisibilityLinkedD] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_D);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityD, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityD };
    });
  const [columnsOrderAddD, setColumnsOrderAddD] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_D);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinkedD, setColumnsOrderLinkedD] = useState<string[]>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_D);
        if (saved) {
          const { order, columnOrder } = saved;
          const storedOrder = Array.isArray(order)
            ? order
            : Array.isArray(columnOrder)
              ? columnOrder
              : null;
          if (Array.isArray(storedOrder)) {
            const normalizedOrder = [...new Set(storedOrder)] as string[];
            return normalizedOrder.includes("actions")
              ? [
                  ...normalizedOrder.filter((id) => id !== "actions"),
                  "actions",
                ]
              : normalizedOrder;
          }
        }
      } catch {
        // ignore
      }
      return [];
    },
  );

  const { data: notRelatedDocuments } = useGetNotRelatedResource<Document>({
    resultType: "document",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsDocuments: ColumnDef<Document, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "documentsDescriptionNumeroEnregistrement",
        header: t("tab_documentsDescriptionNumeroEnregistrement"),
        size: 50,
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.documentsDescriptionNumeroEnregistrement}
            />
          );
        },
      },
      {
        accessorKey: "documentsDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"document"}
            />
          );
        },
      },
      {
        accessorKey: "documentsDescriptionTitre",
        header: t("tab_documentsDescriptionTitre"),
        size: 300,
        cell: ({ row }) => {
          return <HtmlCell value={row.original.documentsDescriptionTitre} />;
        },
      },
      {
        accessorKey: "documentsDescriptionType",
        header: t("tab_documentsDescriptionType"),
        size: 100,
        cell: ({ row }) => {
          return <TextCell value={row.original.documentsDescriptionType} />;
        },
      },
      createActionsColumnDef<Document>(({ row }) => {
          const data = row.original as Document;
          if (!bibliography) return null;
          return (
            <LinkDrawerRowActions
              previewType="document"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("documentsDescriptionNumeroEnregistrement"),
                  value: data.documentsDescriptionNumeroEnregistrement,
                  type: "text",
                },
                {
                  label: t("documentsDescriptionLegendeRedigee"),
                  value: data.documentsDescriptionLegendeRedigee,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <DocumentInBibliographyDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={bibliography.id}
                  type="bibliography"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography: bibliography,
                    document: data,
                    documentsDansBibliographieDescriptionReproduitPage: "",
                    documentsDansBibliographieDescriptionCitePage: "",
                    documentsDansBibliographieDescriptionPhotographe: "",
                    documentsDansBibliographieDescriptionReproductionCouleur:
                      false,
                    documentsDansBibliographieDescriptionNonPagine: false,
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [bibliography, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsDocuments} data={notRelatedDocuments?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notRelatedDocuments?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD_D} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAddD, onColumnOrderChange: setColumnsOrderAddD, state: { columnVisibility: columnVisibilityAddD, columnOrder: columnsOrderAddD } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED_D} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinkedD, onColumnOrderChange: setColumnsOrderLinkedD, state: { columnVisibility: columnVisibilityLinkedD, columnOrder: columnsOrderLinkedD } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_D}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <DocumentLinkGridCard
                  item={row.document}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <DocumentInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="bibliography"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default DocumentInBibliographyLinks;
